import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBoxes, FaEye, FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import productService from '../../utils/productService';
import Loader from '../loader';

const ProductList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const pageSize = 5;
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await productService.getAll({ search: query, page, per_page: pageSize });
      setProducts(response.data || []);
      setMeta(response.meta || { current_page: 1, last_page: 1, total: 0 });
    } catch (error) {
      setProducts([]);
      setMeta({ current_page: 1, last_page: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [query, page]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const handleView = (product) => navigate(`/dashboard/products/${product.id}`);
  const handleEdit = (product) => navigate(`/dashboard/products/${product.id}/edit`);
  const handleAddStock = (product) => navigate(`/dashboard/products/${product.id}/stock`);
  const handleAdd = () => navigate('/dashboard/products/new');
  const handleDelete = async (product) => {
    if (window.confirm(`Delete ${product.name}?`)) {
      await productService.remove(product.id);
      loadProducts();
    }
  };

  const totalStock = products.reduce((sum, product) => sum + (Number(product.stock) || 0), 0);

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
            {t('dashboard.products.listLabel')}
          </p>
          <p className="mt-2 text-sm text-slate-600">
            {t('dashboard.products.summary', { count: meta.total })}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('dashboard.products.searchPlaceholder')}
            className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:w-[320px]"
          />
          <button
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00595A] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004446]"
          >
            <FaPlus className="h-4 w-4" />
            {t('dashboard.products.addButton')}
          </button>
        </div>
      </div>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        {loading ? <Loader /> : 
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.products.name')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.products.stock')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.products.required')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.products.waiting')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.products.price')}</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t('dashboard.products.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {products.map((product) => (
              <tr key={product.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{product.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{product.stock}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{product.required_stock}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{product.customers_waiting}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">${Number(product.price).toFixed(2)}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleView(product)} className="inline-flex items-center justify-center rounded-md bg-slate-100 p-2 text-slate-700 transition hover:bg-slate-200" aria-label={t('dashboard.common.view')} title={t('dashboard.common.view')}>
                      <FaEye className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleEdit(product)} className="inline-flex items-center justify-center rounded-md bg-amber-100 p-2 text-amber-800 transition hover:bg-amber-200" aria-label={t('dashboard.common.edit')} title={t('dashboard.common.edit')}>
                      <FaPencilAlt className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleAddStock(product)} className="inline-flex items-center justify-center rounded-md bg-emerald-100 p-2 text-emerald-800 transition hover:bg-emerald-200" aria-label={t('dashboard.products.addStock')} title={t('dashboard.products.addStock')}>
                      <FaBoxes className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(product)} className="inline-flex items-center justify-center rounded-md bg-rose-100 p-2 text-rose-800 transition hover:bg-rose-200" aria-label={t('dashboard.common.delete')} title={t('dashboard.common.delete')}>
                      <FaTrash className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!products.length ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-500">
                  No products found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        }
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <span>
          Page {meta.current_page} of {meta.last_page}
        </span>
        <div className="flex gap-2">
          <button
            disabled={meta.current_page === 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            className="rounded-md border border-slate-200 px-3 py-2 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            disabled={meta.current_page >= meta.last_page}
            onClick={() => setPage((current) => Math.min(meta.last_page, current + 1))}
            className="rounded-md border border-slate-200 px-3 py-2 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductList;
